import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { X } from 'lucide-react'
import { createFaqGroup, deleteFaqGroup, faqErrorCode, faqErrorMessage, renameFaqGroup } from '../../api/faq'
import useDialogKeys from './useDialogKeys'

const input = 'h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900'

// Create, rename and delete the tenant's FAQ groups. Deleting a group never
// deletes entries: they become ungrouped.
export default function FaqGroupsDialog({ tenantId, groups, maxGroups, onChanged, onClose, onPermissionDenied }) {
  const { t, i18n } = useTranslation()
  const [name, setName] = useState('')
  const [editing, setEditing] = useState(null) // { id, name }
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const root = useRef(null)

  // Escape leaves a rename first, then closes.
  const onKeyDown = useDialogKeys(root, () => (editing ? setEditing(null) : onClose()))

  const run = async (action) => {
    setBusy(true)
    setMessage('')
    try {
      await action()
      onChanged()
    } catch (error) {
      setMessage(faqErrorMessage(t, error))
      if (faqErrorCode(error) === 'permission_denied') onPermissionDenied?.()
    } finally {
      setBusy(false)
    }
  }

  const create = (event) => {
    event.preventDefault()
    if (!name.trim()) return
    run(async () => {
      await createFaqGroup(tenantId, name.trim())
      setName('')
    })
  }

  const rename = () => {
    const next = editing.name.trim()
    if (!next) return
    if (next === groups.find((group) => group.id === editing.id)?.name) {
      setEditing(null) // nothing to change
      return
    }
    run(async () => {
      await renameFaqGroup(tenantId, editing.id, next)
      setEditing(null)
    })
  }

  const remove = (group) => {
    if (!window.confirm(t('faq.groups.deleteConfirm', { name: group.name }))) return
    run(async () => {
      const result = await deleteFaqGroup(tenantId, group.id)
      const formatted = Number(result.moved).toLocaleString(i18n.language)
      toast.success(t('faq.groups.deleted', { count: result.moved, formatted }))
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        ref={root} role="dialog" aria-modal="true" aria-label={t('faq.groups.title')}
        onKeyDown={onKeyDown}
        className="flex max-h-full w-full max-w-md flex-col gap-3 overflow-y-auto rounded-lg bg-white p-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{t('faq.groups.title')}</h2>
          <button type="button" onClick={onClose} aria-label={t('faq.panel.close')}><X size={18} className="text-slate-500" /></button>
        </div>
        <p className="text-xs text-slate-400">{t('faq.groups.count', { count: groups.length, max: maxGroups })}</p>

        <ul className="flex flex-col gap-2">
          {groups.map((group) => (
            <li key={group.id} className="flex items-center gap-2">
              {editing?.id === group.id ? (
                <>
                  <input
                    type="text" value={editing.name} maxLength={40} autoFocus className={input}
                    aria-label={t('faq.groups.nameOf', { name: group.name })}
                    onChange={(event) => setEditing({ id: group.id, name: event.target.value })}
                    onKeyDown={(event) => event.key === 'Enter' && rename()}
                  />
                  <button type="button" onClick={rename} disabled={busy || !editing.name.trim()} className="shrink-0 text-sm font-semibold text-[#1535c8] disabled:opacity-50">
                    {t('faq.panel.save')}
                  </button>
                  <button type="button" onClick={() => setEditing(null)} className="shrink-0 text-sm text-slate-500">
                    {t('faq.panel.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{group.name}</span>
                  <button type="button" onClick={() => setEditing({ id: group.id, name: group.name })} className="shrink-0 text-sm font-medium text-slate-600" aria-label={t('faq.groups.renameOf', { name: group.name })}>
                    {t('faq.row.edit')}
                  </button>
                  <button type="button" onClick={() => remove(group)} disabled={busy} className="shrink-0 text-sm font-medium text-red-600 disabled:opacity-50" aria-label={t('faq.groups.deleteOf', { name: group.name })}>
                    {t('faq.panel.delete')}
                  </button>
                </>
              )}
            </li>
          ))}
          {groups.length === 0 && <li className="text-sm text-slate-400">{t('faq.groups.empty')}</li>}
        </ul>

        {groups.length < maxGroups && (
          <form onSubmit={create} className="flex gap-2">
            <input
              type="text" value={name} maxLength={40} className={input}
              placeholder={t('faq.panel.newGroupName')} aria-label={t('faq.panel.newGroupName')}
              onChange={(event) => setName(event.target.value)}
            />
            <button type="submit" disabled={busy || !name.trim()} className="h-9 shrink-0 rounded-lg bg-[#1535c8] px-3 text-sm font-semibold text-white disabled:opacity-60">
              {t('faq.panel.createGroup')}
            </button>
          </form>
        )}
        {message && <p role="status" aria-live="polite" className="text-xs text-red-600">{message}</p>}
      </div>
    </div>
  )
}
